import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'
import {
  Bot,
  CalendarDays,
  Clock3,
  Euro,
  History,
  Info,
  Loader2,
  MessageCircle,
  MessageSquarePlus,
  PackageCheck,
  Save,
  Send,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  Trash2,
  ToggleLeft,
  ToggleRight,
  UtensilsCrossed,
  X,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { toast } from 'sonner'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { StrapiError, strapiDelete, strapiGet, strapiPost, strapiPut } from '@/lib/strapi'
import { cn } from '@/lib/utils'

type ChatRole = 'user' | 'assistant'

interface AssistantToolResult {
  name: string
  result: unknown
}

interface ChatMessage {
  id: string
  role: ChatRole
  content: string
  toolResults?: AssistantToolResult[]
}

interface AssistantResponse {
  message: string
  toolResults?: AssistantToolResult[]
}

interface SavedChatMessage {
  id: string
  role: ChatRole
  content: string
}

interface SavedChatSummary {
  documentId: string
  title: string
  preview: string
  messageCount: number
  createdAt: string
  updatedAt: string
}

interface SavedChat extends SavedChatSummary {
  messages: SavedChatMessage[]
}

type SelectedModal =
  | { type: 'slot'; payload: any; slot: string; asap?: boolean }
  | { type: 'menuItem'; item: any }
  | { type: 'settings'; settings: any }
  | null

function normalizeDeliveryPricing(flatFee: unknown, chargePerKm: unknown) {
  const deliveryFlatFee = Math.max(0, Number(flatFee ?? 0) || 0)
  const deliveryChargePerKm = deliveryFlatFee > 0
    ? 0
    : Math.max(0, Number(chargePerKm ?? 0) || 0)

  return { deliveryFlatFee, deliveryChargePerKm }
}

const SUGGESTIONS = [
  {
    icon: Clock3,
    label: 'Tonight',
    prompt: 'Check pickup availability for tonight and tell me if ASAP is available.',
  },
  {
    icon: Settings2,
    label: 'Settings',
    prompt: 'Show my current ASAP, pickup, scheduled pickup, and delivery order settings.',
  },
  {
    icon: UtensilsCrossed,
    label: 'Menu',
    prompt: 'Show me menu items that include pizza, with prices and availability.',
  },
  {
    icon: ShoppingBag,
    label: 'Order',
    prompt: 'Help me create a pickup order. Ask me for the customer name, phone, items, and confirmation before creating it.',
  },
]

function messageId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function welcomeMessage(): ChatMessage {
  return {
    id: 'welcome',
    role: 'assistant',
    content:
      'Ask me to check availability, find menu items, create orders, or update menu items. I will show interactive cards when I find useful actions.',
  }
}

function chatTitle(messages: ChatMessage[]) {
  const firstPrompt = messages.find((message) => message.role === 'user')?.content.trim()
  if (!firstPrompt) return 'Assistant chat'
  return firstPrompt.replace(/\s+/g, ' ').slice(0, 100)
}

function formatSavedChatDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function hasWriteTool(toolResults: AssistantToolResult[] = []) {
  return toolResults.some((tool) =>
    tool.name === 'update_menu_item' ||
    tool.name === 'create_customer_order' ||
    tool.name === 'update_order_settings',
  )
}

function resultData(tool: AssistantToolResult) {
  const value: any = tool.result
  return value?.result ?? value
}

function formatMoney(value: unknown, currency = 'EUR') {
  const amount = Number(value ?? 0)
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0)
}

function statusLabel(value: boolean) {
  return value ? 'Enabled' : 'Disabled'
}

function TogglePill({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        enabled
          ? 'bg-[var(--color-accent-green-dim)] text-[var(--color-accent-green)]'
          : 'bg-[var(--color-bg-overlay)] text-[var(--color-text-muted)]',
      )}
    >
      {enabled ? <ToggleRight className="h-3 w-3" /> : <ToggleLeft className="h-3 w-3" />}
      {statusLabel(enabled)}
    </span>
  )
}

function AssistantMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        table: ({ children }) => (
          <div className="my-2 max-w-full overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border-subtle)]">
            <table className="w-full min-w-max border-collapse text-left text-xs">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border-b border-r border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-1.5 font-semibold text-[var(--color-text-primary)] last:border-r-0">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border-b border-r border-[var(--color-border-subtle)] px-2 py-1.5 align-top last:border-r-0">
            {children}
          </td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

export function AssistantWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([welcomeMessage()])
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [activeSavedId, setActiveSavedId] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [savedChats, setSavedChats] = useState<SavedChatSummary[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [loadingChatId, setLoadingChatId] = useState<string | null>(null)
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null)
  const [selectedModal, setSelectedModal] = useState<SelectedModal>(null)
  const [settingsDraft, setSettingsDraft] = useState<Record<string, any>>({})
  const [priceDraft, setPriceDraft] = useState('')
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const apiHistory = useMemo(
    () =>
      messages
        .filter((message) => message.id !== 'welcome')
        .map(({ role, content }) => ({ role, content })),
    [messages],
  )

  useEffect(() => {
    if (!open) return
    scrollerRef.current?.scrollTo({
      top: scrollerRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [messages, isSending, open])

  useEffect(() => {
    if (!open) return
    const id = window.setTimeout(() => textareaRef.current?.focus(), 80)
    return () => window.clearTimeout(id)
  }, [open])

  async function sendMessage(text: string) {
    const trimmed = text.trim()
    if (!trimmed || isSending || isSaving) return

    const userMessage: ChatMessage = { id: messageId(), role: 'user', content: trimmed }
    const nextHistory = [...apiHistory, { role: 'user' as const, content: trimmed }]
    setMessages((current) => [...current, userMessage])
    setIsDirty(true)
    setInput('')
    setIsSending(true)

    try {
      const response = await strapiPost<AssistantResponse>('/api/assistant/chat', {
        messages: nextHistory,
      })
      const data = (response as any)?.data ?? response
      setMessages((current) => [
        ...current,
        {
          id: messageId(),
          role: 'assistant',
          content: data.message || 'Done.',
          toolResults: data.toolResults ?? [],
        },
      ])
      if (hasWriteTool(data.toolResults)) {
        toast.success('Assistant action completed')
      }
    } catch (error) {
      const message =
        error instanceof StrapiError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Assistant request failed'
      toast.error(message)
      setMessages((current) => [
        ...current,
        {
          id: messageId(),
          role: 'assistant',
          content:
            message === 'DeepSeek API key is not configured.'
              ? 'DeepSeek is not configured yet. Add DEEPSEEK_API_KEY to the backend environment and restart the server.'
              : `I could not complete that request: ${message}`,
        },
      ])
    } finally {
      setIsSending(false)
      textareaRef.current?.focus()
    }
  }

  async function loadSavedChats() {
    setIsLoadingHistory(true)
    try {
      const response = await strapiGet<SavedChatSummary[]>('/api/assistant/history')
      setSavedChats(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load saved chats')
    } finally {
      setIsLoadingHistory(false)
    }
  }

  async function openSavedChat(documentId: string) {
    if (isDirty && messages.some((message) => message.role === 'user') &&
      !window.confirm('This chat has unsaved changes. Open another chat and discard them?')) return
    setLoadingChatId(documentId)
    try {
      const response = await strapiGet<SavedChat>(`/api/assistant/history/${encodeURIComponent(documentId)}`)
      const chat = response.data
      const restoredMessages = Array.isArray(chat.messages)
        ? chat.messages
            .filter((message) => (message.role === 'user' || message.role === 'assistant') && typeof message.content === 'string')
            .map((message) => ({ id: message.id || messageId(), role: message.role, content: message.content }))
        : []
      setMessages(restoredMessages.length > 0 ? restoredMessages : [welcomeMessage()])
      setActiveSavedId(chat.documentId)
      setIsDirty(false)
      setShowHistory(false)
      setInput('')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not open saved chat')
    } finally {
      setLoadingChatId(null)
    }
  }

  async function saveConversation() {
    const savedMessages: SavedChatMessage[] = messages
      .filter((message) => message.id !== 'welcome')
      .map(({ id, role, content }) => ({ id, role, content }))
    if (!savedMessages.some((message) => message.role === 'user') || isSending || isSaving) return

    setIsSaving(true)
    try {
      const payload = { title: chatTitle(messages), messages: savedMessages }
      const response = activeSavedId
        ? await strapiPut<SavedChat>(`/api/assistant/history/${encodeURIComponent(activeSavedId)}`, payload)
        : await strapiPost<SavedChat>('/api/assistant/history', payload)
      const saved = response.data
      setActiveSavedId(saved.documentId)
      setIsDirty(false)
      const summary: SavedChatSummary = {
        documentId: saved.documentId,
        title: saved.title,
        preview: saved.preview,
        messageCount: saved.messageCount,
        createdAt: saved.createdAt,
        updatedAt: saved.updatedAt,
      }
      setSavedChats((current) => [summary, ...current.filter((chat) => chat.documentId !== saved.documentId)])
      toast.success(activeSavedId ? 'Chat saved' : 'Chat added to history')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save chat')
    } finally {
      setIsSaving(false)
    }
  }

  async function deleteSavedChat(chat: SavedChatSummary) {
    if (!window.confirm(`Delete “${chat.title}” from saved chat history?`)) return
    setDeletingChatId(chat.documentId)
    try {
      await strapiDelete(`/api/assistant/history/${encodeURIComponent(chat.documentId)}`)
      setSavedChats((current) => current.filter((item) => item.documentId !== chat.documentId))
      if (activeSavedId === chat.documentId) {
        setActiveSavedId(null)
        if (isDirty) {
          setIsDirty(messages.some((message) => message.role === 'user'))
        } else {
          setMessages([welcomeMessage()])
          setIsDirty(false)
          setInput('')
        }
      }
      toast.success('Saved chat deleted')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete saved chat')
    } finally {
      setDeletingChatId(null)
    }
  }

  function startNewChat() {
    if (isSending || isSaving) return
    if (isDirty && messages.some((message) => message.role === 'user') &&
      !window.confirm('This chat has unsaved changes. Start a new chat and discard them?')) return
    setMessages([welcomeMessage()])
    setActiveSavedId(null)
    setIsDirty(false)
    setInput('')
    setShowHistory(false)
  }

  function submitConfirmedSettings(settings: Record<string, any>) {
    const deliveryPricing = normalizeDeliveryPricing(settings.deliveryFlatFee, settings.deliveryChargePerKm)
    const payload = {
      acceptAsapOrders: settings.acceptAsapOrders === true,
      allowCollectionOrders: settings.allowCollectionOrders === true,
      allowScheduledCollectionOrders: settings.allowScheduledCollectionOrders === true,
      deliveryFlatFee: deliveryPricing.deliveryFlatFee,
      deliveryChargePerKm: deliveryPricing.deliveryChargePerKm,
    }
    setSelectedModal(null)
    sendMessage(
      `I confirm this exact order settings change. Call update_order_settings with confirmed=true, acceptAsapOrders=${payload.acceptAsapOrders}, allowCollectionOrders=${payload.allowCollectionOrders}, allowScheduledCollectionOrders=${payload.allowScheduledCollectionOrders}, deliveryFlatFee=${payload.deliveryFlatFee}, deliveryChargePerKm=${payload.deliveryChargePerKm}. Do not ask for another confirmation.`,
    )
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    sendMessage(input)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      sendMessage(input)
    }
  }

  function openSettings(settings: any) {
    setSettingsDraft({
      acceptAsapOrders: settings.acceptAsapOrders === true,
      allowCollectionOrders: settings.allowCollectionOrders === true,
      allowScheduledCollectionOrders: settings.allowScheduledCollectionOrders !== false,
      deliveryFlatFee: Number(settings.deliveryFlatFee ?? 0) || 0,
      deliveryChargePerKm: Number(settings.deliveryChargePerKm ?? 0) || 0,
    })
    setSelectedModal({ type: 'settings', settings })
  }

  function openMenuItem(item: any) {
    setPriceDraft(String(Number(item.basePrice ?? 0) || 0))
    setSelectedModal({ type: 'menuItem', item })
  }

  return (
    <div className="fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-3 z-50 sm:right-6 md:bottom-6">
      {open && (
        <section className="mb-3 flex h-[min(720px,calc(100dvh-11rem-env(safe-area-inset-bottom)))] w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-2xl sm:w-[430px]">
          <header className="flex items-center gap-3 border-b border-[var(--color-border-subtle)] px-3 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]">
              <Bot className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">{showHistory ? 'Saved chats' : 'Assistant'}</h2>
              <p className="truncate text-xs text-[var(--color-text-muted)]">
                {showHistory ? 'Only chats you save are kept' : 'Availability, menu, and orders'}
              </p>
            </div>
            {showHistory ? (
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                aria-label="Back to chat"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
              >
                <MessageCircle className="h-4 w-4" />
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={saveConversation}
                  disabled={!messages.some((message) => message.role === 'user') || !isDirty || isSending || isSaving}
                  aria-label={activeSavedId ? 'Save chat changes' : 'Save chat'}
                  title={activeSavedId ? 'Save changes to this chat' : 'Save this chat'}
                  className="inline-flex h-8 shrink-0 items-center gap-1 rounded-[var(--radius-md)] px-2 text-xs font-medium text-[var(--color-accent-amber)] transition-colors hover:bg-[var(--color-bg-elevated)] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span>{activeSavedId && !isDirty ? 'Saved' : 'Save'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowHistory(true)
                    void loadSavedChats()
                  }}
                  disabled={isSending || isSaving}
                  aria-label="Saved chat history"
                  title="Saved chat history"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
                >
                  <History className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={startNewChat}
                  disabled={isSending || isSaving}
                  aria-label="Start a new chat"
                  title="New chat"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
                >
                  <MessageSquarePlus className="h-4 w-4" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
              className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          {showHistory ? (
            <div className="flex-1 space-y-2 overflow-y-auto px-3 py-4">
              {isLoadingHistory ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--color-text-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading saved chats
                </div>
              ) : savedChats.length === 0 ? (
                <div className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-border-subtle)] px-4 py-8 text-center">
                  <History className="mx-auto h-6 w-6 text-[var(--color-text-muted)]" />
                  <p className="mt-3 text-sm font-medium text-[var(--color-text-primary)]">No saved chats yet</p>
                  <p className="mt-1 text-xs leading-5 text-[var(--color-text-muted)]">Chats appear here only after you press Save.</p>
                </div>
              ) : savedChats.map((chat) => (
                <div key={chat.documentId} className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
                  <button
                    type="button"
                    onClick={() => void openSavedChat(chat.documentId)}
                    disabled={!!loadingChatId || !!deletingChatId || isSending || isSaving}
                    className="min-w-0 flex-1 text-left disabled:opacity-60"
                  >
                    <span className="block truncate text-sm font-medium text-[var(--color-text-primary)]">{chat.title}</span>
                    <span className="mt-1 block line-clamp-2 text-xs leading-5 text-[var(--color-text-secondary)]">{chat.preview}</span>
                    <span className="mt-2 block text-[11px] text-[var(--color-text-muted)]">
                      {loadingChatId === chat.documentId ? 'Opening…' : `${chat.messageCount} messages · ${formatSavedChatDate(chat.updatedAt)}`}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteSavedChat(chat)}
                    disabled={!!deletingChatId || !!loadingChatId}
                    aria-label={`Delete ${chat.title}`}
                    title="Delete saved chat"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-accent-red-dim)] hover:text-[var(--color-accent-red)] disabled:opacity-50"
                  >
                    {deletingChatId === chat.documentId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>
          ) : (
          <div ref={scrollerRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn('flex gap-2', message.role === 'user' ? 'justify-end' : 'justify-start')}
              >
                {message.role === 'assistant' && (
                  <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]">
                    <Bot className="h-4 w-4" />
                  </div>
                )}
                <div className={cn('min-w-0', message.role === 'user' ? 'max-w-[82%]' : 'max-w-[calc(100%-2.25rem)]')}>
                  <div
                    className={cn(
                      'rounded-[var(--radius-lg)] px-3 py-2 text-sm leading-5',
                      message.role === 'user'
                        ? 'whitespace-pre-wrap bg-[var(--color-accent-amber)] text-[#1a1714]'
                        : 'assistant-markdown border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]',
                    )}
                  >
                    {message.role === 'assistant' ? <AssistantMarkdown content={message.content} /> : message.content}
                  </div>
                  {message.role === 'assistant' && message.toolResults && message.toolResults.length > 0 && (
                    <ToolResultCards
                      tools={message.toolResults}
                      onSlot={(payload, slot, asap) => setSelectedModal({ type: 'slot', payload, slot, asap })}
                      onMenuItem={openMenuItem}
                      onSettings={openSettings}
                      onSend={sendMessage}
                    />
                  )}
                </div>
              </div>
            ))}

            {isSending && (
              <div className="flex gap-2">
                <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="inline-flex items-center gap-2 rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-secondary)]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Thinking
                </div>
              </div>
            )}
          </div>
          )}

          {!showHistory && <div className="border-t border-[var(--color-border-subtle)] px-3 py-3">
            <div className="mb-2 grid grid-cols-4 gap-1.5">
              {SUGGESTIONS.map(({ icon: Icon, label, prompt }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => sendMessage(prompt)}
                  disabled={isSending || isSaving}
                  className="flex h-9 items-center justify-center gap-1 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-2 text-xs font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-overlay)] hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
            <form onSubmit={handleSubmit} className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSaving}
                rows={1}
                placeholder="Ask the assistant..."
                className="max-h-28 min-h-[42px] flex-1 resize-none rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none transition-colors placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent-amber)]"
              />
              <Button type="submit" size="icon" disabled={!input.trim() || isSaving} loading={isSending} aria-label="Send message">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>}
        </section>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? 'Close assistant' : 'Open assistant'}
        aria-expanded={open}
        className="ml-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-accent-amber)] text-[#1a1714] shadow-xl shadow-black/20 transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-amber)]/50"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      <AssistantModal
        selected={selectedModal}
        settingsDraft={settingsDraft}
        priceDraft={priceDraft}
        onSettingsDraft={setSettingsDraft}
        onPriceDraft={setPriceDraft}
        onClose={() => setSelectedModal(null)}
        onSend={sendMessage}
        onSubmitSettings={submitConfirmedSettings}
      />
    </div>
  )
}

function ToolResultCards({
  tools,
  onSlot,
  onMenuItem,
  onSettings,
  onSend,
}: {
  tools: AssistantToolResult[]
  onSlot: (payload: any, slot: string, asap?: boolean) => void
  onMenuItem: (item: any) => void
  onSettings: (settings: any) => void
  onSend: (message: string) => void
}) {
  return (
    <div className="mt-2 space-y-2">
      {tools.map((tool, index) => {
        const data = resultData(tool)
        if (tool.name === 'check_order_availability') {
          return <OrderAvailabilityCard key={`${tool.name}-${index}`} data={data} onSlot={onSlot} />
        }
        if (tool.name === 'check_availability') {
          return <BookingAvailabilityCard key={`${tool.name}-${index}`} data={data} onSlot={onSlot} />
        }
        if (tool.name === 'list_menu_items' && Array.isArray(data)) {
          return <MenuItemsCard key={`${tool.name}-${index}`} items={data} onMenuItem={onMenuItem} onSend={onSend} />
        }
        if (tool.name === 'get_restaurant_status' || tool.name === 'update_order_settings') {
          return <SettingsCard key={`${tool.name}-${index}`} settings={data} onSettings={onSettings} onSend={onSend} />
        }
        if (tool.name === 'update_menu_item' || tool.name === 'create_customer_order') {
          return <SuccessCard key={`${tool.name}-${index}`} tool={tool.name} data={data} />
        }
        return null
      })}
    </div>
  )
}

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)]">
      {children}
    </div>
  )
}

function OrderAvailabilityCard({ data, onSlot }: { data: any; onSlot: (payload: any, slot: string, asap?: boolean) => void }) {
  const slots: string[] = Array.isArray(data?.slots) ? data.slots : []
  const fulfillment = data?.fulfillmentMethod === 'delivery' ? 'delivery' : 'pickup'
  return (
    <CardShell>
      <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border-subtle)] px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
            {fulfillment === 'delivery' ? 'Delivery availability' : 'Pickup availability'}
          </p>
          <p className="text-xs text-[var(--color-text-muted)]">{data?.date ?? 'Selected date'}</p>
        </div>
        <PackageCheck className="h-4 w-4 shrink-0 text-[var(--color-accent-amber)]" />
      </div>
      <div className="space-y-3 p-3">
        {data?.asapAvailable && (
          <button
            type="button"
            onClick={() => onSlot(data, data.asapSlot ?? 'ASAP', true)}
            className="flex w-full items-center justify-between rounded-[var(--radius-md)] bg-[var(--color-accent-green-dim)] px-3 py-2 text-left text-sm text-[var(--color-accent-green)]"
          >
            <span className="font-semibold">ASAP available</span>
            <span>{data.asapSlot ?? 'Now'}</span>
          </button>
        )}
        {slots.length > 0 ? (
          <div className="grid grid-cols-3 gap-1.5">
            {slots.slice(0, 18).map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() => onSlot(data, slot)}
                className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-2 py-1.5 text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-accent-amber)] hover:text-[var(--color-accent-amber)]"
              >
                {slot}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">No slots found for this date.</p>
        )}
      </div>
    </CardShell>
  )
}

function BookingAvailabilityCard({ data, onSlot }: { data: any; onSlot: (payload: any, slot: string) => void }) {
  const slots: any[] = Array.isArray(data?.availableSlots) ? data.availableSlots : []
  return (
    <CardShell>
      <div className="flex items-center justify-between border-b border-[var(--color-border-subtle)] px-3 py-2">
        <div>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">Booking availability</p>
          <p className="text-xs text-[var(--color-text-muted)]">{data?.date ?? 'Selected date'}</p>
        </div>
        <CalendarDays className="h-4 w-4 text-[var(--color-accent-amber)]" />
      </div>
      <div className="grid grid-cols-3 gap-1.5 p-3">
        {slots.slice(0, 18).map((slot) => (
          <button
            key={slot.time ?? JSON.stringify(slot)}
            type="button"
            onClick={() => onSlot(data, slot.time)}
            className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-2 py-1.5 text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-accent-amber)] hover:text-[var(--color-accent-amber)]"
          >
            {slot.time}
          </button>
        ))}
      </div>
    </CardShell>
  )
}

function MenuItemsCard({ items, onMenuItem, onSend }: { items: any[]; onMenuItem: (item: any) => void; onSend: (message: string) => void }) {
  return (
    <CardShell>
      <div className="flex items-center justify-between border-b border-[var(--color-border-subtle)] px-3 py-2">
        <div>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">Menu items</p>
          <p className="text-xs text-[var(--color-text-muted)]">{items.length} result{items.length === 1 ? '' : 's'}</p>
        </div>
        <UtensilsCrossed className="h-4 w-4 text-[var(--color-accent-amber)]" />
      </div>
      <div className="divide-y divide-[var(--color-border-subtle)]">
        {items.slice(0, 8).map((item) => (
          <button
            key={item.documentId}
            type="button"
            onClick={() => onMenuItem(item)}
            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-[var(--color-bg-elevated)]"
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-[var(--color-text-primary)]">{item.name}</span>
              <span className="block truncate text-xs text-[var(--color-text-muted)]">
                {item.category ?? 'Uncategorized'} · {item.isAvailable ? 'available' : 'unavailable'}
              </span>
            </span>
            <span className="shrink-0 text-sm font-semibold text-[var(--color-text-primary)]">{formatMoney(item.basePrice)}</span>
          </button>
        ))}
      </div>
      <div className="border-t border-[var(--color-border-subtle)] p-2">
        <Button
          size="sm"
          variant="secondary"
          className="w-full"
          onClick={() => onSend('Help me create an order from these menu items. Ask for customer name, phone, quantities, notes, and confirmation before creating it.')}
        >
          Start order from menu
        </Button>
      </div>
    </CardShell>
  )
}

function SettingsCard({ settings, onSettings, onSend }: { settings: any; onSettings: (settings: any) => void; onSend: (message: string) => void }) {
  const deliveryFlatFee = Number(settings?.deliveryFlatFee ?? 0) || 0
  const deliveryChargePerKm = Number(settings?.deliveryChargePerKm ?? 0) || 0
  const deliveryConfigured = deliveryFlatFee > 0 || deliveryChargePerKm > 0
  return (
    <CardShell>
      <div className="flex items-center justify-between border-b border-[var(--color-border-subtle)] px-3 py-2">
        <div>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">Order settings</p>
          <p className="text-xs text-[var(--color-text-muted)]">{settings?.restaurantName ?? 'Restaurant'}</p>
        </div>
        <Settings2 className="h-4 w-4 text-[var(--color-accent-amber)]" />
      </div>
      <div className="space-y-2 p-3">
        <SettingRow label="ASAP orders" enabled={settings?.acceptAsapOrders === true} />
        <SettingRow label="Pickup orders" enabled={settings?.allowCollectionOrders === true} />
        <SettingRow label="Scheduled pickup" enabled={settings?.allowScheduledCollectionOrders !== false} />
        <SettingRow label="Delivery pricing" enabled={deliveryConfigured} detail={deliveryFlatFee > 0 ? formatMoney(deliveryFlatFee) : deliveryChargePerKm > 0 ? `${formatMoney(deliveryChargePerKm)}/km` : 'No fee'} />
      </div>
      <div className="grid grid-cols-2 gap-2 border-t border-[var(--color-border-subtle)] p-2">
        <Button size="sm" variant="secondary" onClick={() => onSettings(settings)}>
          Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onSend('Check pickup and delivery order availability for today.')}>
          Check slots
        </Button>
      </div>
    </CardShell>
  )
}

function SettingRow({ label, enabled, detail }: { label: string; enabled: boolean; detail?: string }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] px-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">{label}</p>
        {detail && <p className="truncate text-xs text-[var(--color-text-muted)]">{detail}</p>}
      </div>
      <TogglePill enabled={enabled} />
    </div>
  )
}

function SuccessCard({ tool, data }: { tool: string; data: any }) {
  return (
    <CardShell>
      <div className="flex items-center gap-2 p-3 text-sm text-[var(--color-text-primary)]">
        <PackageCheck className="h-4 w-4 text-[var(--color-accent-green)]" />
        <span className="font-medium">
          {tool === 'create_customer_order'
            ? `Order created${data?.documentId ? `: ${data.documentId}` : ''}`
            : tool === 'update_order_settings'
              ? 'Order settings updated'
              : 'Menu item updated'}
        </span>
      </div>
    </CardShell>
  )
}

function AssistantModal({
  selected,
  settingsDraft,
  priceDraft,
  onSettingsDraft,
  onPriceDraft,
  onClose,
  onSend,
  onSubmitSettings,
}: {
  selected: SelectedModal
  settingsDraft: Record<string, any>
  priceDraft: string
  onSettingsDraft: (value: Record<string, any>) => void
  onPriceDraft: (value: string) => void
  onClose: () => void
  onSend: (message: string) => void
  onSubmitSettings: (settings: Record<string, any>) => void
}) {
  if (!selected) return null

  if (selected.type === 'slot') {
    const fulfillment = selected.payload?.fulfillmentMethod === 'delivery' ? 'delivery' : 'pickup'
    const label = selected.asap ? 'ASAP' : selected.slot
    return (
      <Modal open onClose={onClose} title={`${fulfillment === 'delivery' ? 'Delivery' : 'Pickup'} slot`}>
        <div className="space-y-4">
          <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-4">
            <p className="text-sm text-[var(--color-text-muted)]">Selected time</p>
            <p className="mt-1 text-2xl font-semibold text-[var(--color-text-primary)]">{label}</p>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{selected.payload?.date}</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              onClick={() => {
                onClose()
                onSend(
                  selected.asap
                    ? `Help me build an ASAP ${fulfillment} order. Ask for customer name, phone, items, and confirmation before creating it.`
                    : `Help me build a scheduled ${fulfillment} order for ${selected.payload?.date} at ${selected.slot}. Ask for customer name, phone, items, and confirmation before creating it.`,
                )
              }}
            >
              Build order
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  if (selected.type === 'menuItem') {
    const item = selected.item
    return (
      <Modal open onClose={onClose} title={item.name}>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-3">
              <p className="text-xs text-[var(--color-text-muted)]">Price</p>
              <p className="text-lg font-semibold text-[var(--color-text-primary)]">{formatMoney(item.basePrice)}</p>
            </div>
            <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-3">
              <p className="text-xs text-[var(--color-text-muted)]">Availability</p>
              <div className="mt-1"><TogglePill enabled={item.isAvailable === true} /></div>
            </div>
          </div>
          {item.description && <p className="text-sm leading-6 text-[var(--color-text-secondary)]">{item.description}</p>}
          {(item.variants?.length > 0 || item.modifiers?.length > 0) && (
            <div className="space-y-2 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] p-3">
              {item.variants?.length > 0 && (
                <p className="text-sm text-[var(--color-text-secondary)]">Variants: {item.variants.map((variant: any) => variant.name).join(', ')}</p>
              )}
              {item.modifiers?.length > 0 && (
                <p className="text-sm text-[var(--color-text-secondary)]">Options: {item.modifiers.map((modifier: any) => modifier.name).join(', ')}</p>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <input
              value={priceDraft}
              onChange={(event) => onPriceDraft(event.target.value)}
              inputMode="decimal"
              className="h-10 min-w-0 flex-1 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-amber)]"
            />
            <Button
              variant="secondary"
              onClick={() => {
                onClose()
                onSend(`I confirm: call update_menu_item with confirmed=true, menuItemDocumentId=${item.documentId}, basePrice=${Number(priceDraft) || 0}. Do not ask for another confirmation.`)
              }}
            >
              Save price
            </Button>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              variant={item.isAvailable ? 'danger' : 'success'}
              onClick={() => {
                onClose()
                onSend(`I confirm: call update_menu_item with confirmed=true, menuItemDocumentId=${item.documentId}, isAvailable=${!item.isAvailable}. Do not ask for another confirmation.`)
              }}
            >
              {item.isAvailable ? 'Disable item' : 'Enable item'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                onClose()
                onSend(`Add ${item.name} to a new pickup order. Ask for quantity, customer name, phone, and confirmation before creating it.`)
              }}
            >
              Add to order
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open onClose={onClose} title="Order settings">
      <div className="space-y-4">
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] p-3">
          <p className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
            <Info className="h-4 w-4 text-[var(--color-accent-amber)]" />
            These controls update the same customer-page and restaurant settings used by online orders.
          </p>
        </div>
        <div className="space-y-2">
          {[
            ['acceptAsapOrders', 'ASAP orders'],
            ['allowCollectionOrders', 'Pickup orders'],
            ['allowScheduledCollectionOrders', 'Scheduled pickup'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => onSettingsDraft({ ...settingsDraft, [key]: !settingsDraft[key] })}
              className="flex w-full items-center justify-between rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] px-3 py-2 text-left"
            >
              <span className="text-sm font-medium text-[var(--color-text-primary)]">{label}</span>
              <TogglePill enabled={settingsDraft[key] === true} />
            </button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1">
            <span className="flex items-center gap-1 text-xs font-medium text-[var(--color-text-muted)]">
              <Euro className="h-3.5 w-3.5" />
              Flat delivery fee
            </span>
            <input
              value={settingsDraft.deliveryFlatFee ?? 0}
              onChange={(event) => {
                const value = event.target.value
                onSettingsDraft({
                  ...settingsDraft,
                  deliveryFlatFee: value,
                  deliveryChargePerKm: Number(value) > 0 ? 0 : settingsDraft.deliveryChargePerKm,
                })
              }}
              inputMode="decimal"
              className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-amber)]"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs font-medium text-[var(--color-text-muted)]">Per-km delivery fee</span>
            <input
              value={settingsDraft.deliveryChargePerKm ?? 0}
              onChange={(event) => {
                const value = event.target.value
                onSettingsDraft({
                  ...settingsDraft,
                  deliveryFlatFee: Number(value) > 0 ? 0 : settingsDraft.deliveryFlatFee,
                  deliveryChargePerKm: value,
                })
              }}
              inputMode="decimal"
              className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent-amber)]"
            />
          </label>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Button onClick={() => onSubmitSettings(settingsDraft)}>Save settings</Button>
          <Button
            variant="secondary"
            onClick={() => onSettingsDraft({ ...settingsDraft, deliveryFlatFee: 0, deliveryChargePerKm: 0 })}
          >
            Disable delivery fee
          </Button>
        </div>
      </div>
    </Modal>
  )
}
